import { useRef } from "react";
import { motion, useScroll } from "framer-motion";
import Reveal from "@/components/Reveal";
import { PhoneCall } from "lucide-react";

const stages = [
  { n: "01", title: "Discover call", text: "A short, direct conversation about what you want to be known for and where your presence stands today. It's also where we both decide whether this is the right fit." },
  { n: "02", title: "Onboarding", text: "I deep-dive into your expertise, voice, audience and references. You talk — I listen, question and map." },
  { n: "03", title: "Position", text: "I define your point of view, positioning and content direction." },
  { n: "04", title: "Build", text: "I create the content, creative and LinkedIn system around it. You review and approve — I handle everything else." },
  { n: "05", title: "Grow", text: "We publish, learn, refine and build the presence over time — with me in the room throughout." },
];

export default function Process() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 70%", "end 70%"] });

  return (
    <section id="how-it-works" className="bg-[#F3F0E8] text-[#09090B] scroll-mt-20">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-28 sm:py-40">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-[#118C4F]">process</p>
          <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2 mt-6">
            <h2
              data-testid="process-heading"
              className="font-serif-d font-black tracking-tight leading-[1.0] text-4xl sm:text-6xl lg:text-8xl"
            >
              How it works.
            </h2>
            <p className="font-serif-d italic font-semibold text-2xl sm:text-3xl text-[#09090B]/40">
              Simple on purpose.
            </p>
          </div>
        </Reveal>

        <div ref={ref} className="relative mt-20 sm:mt-28">
          <div className="absolute left-[26px] sm:left-[42px] top-0 bottom-0 w-px bg-[#09090B]/12" aria-hidden="true" />
          <motion.div
            style={{ scaleY: scrollYProgress }}
            className="absolute left-[26px] sm:left-[42px] top-0 bottom-0 w-px bg-[#118C4F] origin-top"
            aria-hidden="true"
          />

          {stages.map((s, i) => (
            <Reveal key={s.n} delay={0.05}>
              <div
                data-testid={`process-step-${i + 1}`}
                className="group relative pl-20 sm:pl-32 pb-16 sm:pb-24 last:pb-0"
              >
                <span
                  aria-hidden="true"
                  className="absolute left-0 top-0 w-[53px] h-[53px] sm:w-[85px] sm:h-[85px] border border-[#09090B]/20 bg-[#F3F0E8] group-hover:border-[#118C4F] group-hover:bg-[#118C4F] flex items-center justify-center transition-colors duration-500"
                >
                  {i === 0 ? (
                    <PhoneCall className="w-5 h-5 sm:w-7 sm:h-7 text-[#118C4F] group-hover:text-[#F3F0E8] transition-colors duration-500" strokeWidth={1.8} />
                  ) : (
                    <span className="font-serif-d font-black text-lg sm:text-3xl group-hover:text-[#F3F0E8] transition-colors duration-500">
                      {s.n}
                    </span>
                  )}
                </span>
                <h3 className="font-serif-d font-black tracking-tight uppercase text-3xl sm:text-5xl lg:text-6xl group-hover:text-[#118C4F] transition-colors duration-500">
                  {s.title}
                </h3>
                <p className="text-sm sm:text-lg text-[#09090B]/60 leading-relaxed mt-3 max-w-xl">
                  {s.text}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
